import type { Inline, LegalContent } from "./legal";

const t = (text: string): Inline => ({ t: "text", text });
const b = (text: string): Inline => ({ t: "b", text });
const em = (): Inline => ({ t: "email", text: "info@quintaratech.com" });
const a = (text: string, href: string): Inline => ({ t: "a", text, href });
const link = (text: string, to: string): Inline => ({ t: "link", text, to });

export const defaultPrivacyContent: LegalContent = {
  meta: {
    title: "Quintara Privacy Policy",
    description: "How Quintara Advertising Services L.L.C collects, uses and protects personal data.",
  },
  hero: { eyebrow: "Legal", heading: "Privacy policy", dateLabel: "Last updated 2 October 2026" },
  body: [
    {
      kind: "p",
      spans: [
        t(
          'This policy explains how Quintara Advertising Services L.L.C ("Quintara", "we", "us") handles personal data when you visit this website, contact us, or use our business messaging services. We process personal data in line with the UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data and other laws that apply in the countries where we operate.',
        ),
      ],
    },
    { kind: "h2", text: "Who we are" },
    {
      kind: "p",
      spans: [
        t(
          "Quintara Advertising Services L.L.C is a limited liability company registered in Dubai, United Arab Emirates (Commercial Licence No. 1648840), with its registered office at Office 4C-07, Al Ghurair Real Estate, Al Muteena, Dubai, United Arab Emirates.",
        ),
      ],
    },
    { kind: "h2", text: "Two roles we play" },
    {
      kind: "ul",
      items: [
        [b("Controller."), t(" For data about website visitors, people who contact us, and contacts at our clients and partners, Quintara decides how and why the data is used.")],
        [b("Processor."), t(" When businesses use our platform to message their own customers on WhatsApp, the business is the controller of its customers' data and we process it only on its documented instructions, under a data processing agreement.")],
      ],
    },
    { kind: "h2", text: "What we collect" },
    {
      kind: "ul",
      items: [
        [b("Enquiries:"), t(" name, work email, company, phone number, country, the type of organization, expected message volumes and what you write to us.")],
        [b("Client account data:"), t(" business contact details, billing information, and the information needed for WhatsApp business verification.")],
        [b("Messaging data (as processor):"), t(" phone numbers, message content, delivery and read status, opt-in and opt-out records, and conversation metadata.")],
        [b("Technical data:"), t(" standard server logs such as IP address, browser type and the pages requested, kept for security and troubleshooting.")],
      ],
    },
    {
      kind: "p",
      spans: [t("This website does not use advertising or analytics cookies. It loads fonts from Google Fonts, which receives your IP address when the page loads.")],
    },
    { kind: "h2", text: "How we use it" },
    {
      kind: "ul",
      items: [
        [t("To answer your enquiry and discuss our services with you (your consent and our legitimate interest in responding).")],
        [t("To provide, secure and support the platform for our clients (performance of a contract).")],
        [t("To meet legal, tax and regulatory obligations, and the requirements of the WhatsApp Business Platform.")],
        [t("To detect and prevent spam, fraud and abuse of the messaging channel.")],
      ],
    },
    {
      kind: "p",
      spans: [t("We do not sell personal data. We do not use our clients' messaging data for our own marketing or to train models for anyone other than that client.")],
    },
    { kind: "h2", text: "WhatsApp and Meta" },
    {
      kind: "p",
      spans: [
        t("Messages sent through our platform travel over the WhatsApp Business Platform operated by Meta Platforms, Inc. and its affiliates. Meta processes that data under its own terms and policies, including the "),
        a("WhatsApp Business Policy", "https://www.whatsapp.com/legal/business-policy"),
        t(". Businesses using Quintara must collect valid opt-in from their customers before messaging them."),
      ],
    },
    { kind: "h2", text: "Who we share it with" },
    {
      kind: "ul",
      items: [
        [t("Meta, to deliver WhatsApp messages.")],
        [t("Cloud hosting and infrastructure providers, including Amazon Web Services.")],
        [t("Companies in the Numbase Group and JGroup that help deliver our services, under contracts that limit their use of the data.")],
        [t("Authorities, when the law requires it.")],
      ],
    },
    { kind: "h2", text: "Where it is stored" },
    {
      kind: "p",
      spans: [t("For Gulf clients we host data in AWS regions in the UAE and Bahrain. Where data must move to another country, we use contractual and technical safeguards that the law requires.")],
    },
    { kind: "h2", text: "How long we keep it" },
    {
      kind: "p",
      spans: [t("Enquiry data is kept for up to 24 months after our last contact unless you become a client. Client and messaging data is kept for as long as the client's contract and its instructions require, then deleted or returned. Records we must keep by law are kept for the legal period.")],
    },
    { kind: "h2", text: "Job applicants", id: "applicants" },
    {
      kind: "p",
      spans: [
        t("If you apply through our careers page, we use your application details and CV only for recruitment: to assess you for the team or role you applied for, and for other current or future roles at Quintara. We keep applications for up to 12 months and then delete them. To ask a question, or to have your application deleted sooner, email "),
        em(),
        t("."),
      ],
    },
    { kind: "h2", text: "Security" },
    {
      kind: "p",
      spans: [t("We use encryption in transit and at rest, strict separation between client accounts, least-privilege access, and continuous monitoring.")],
    },
    { kind: "h2", text: "Your rights" },
    {
      kind: "p",
      spans: [
        t("You can ask to access, correct or delete your personal data, to restrict or object to its use, to receive a copy in a portable format, and to withdraw consent at any time. If your data was processed by a business that messaged you through our platform, please contact that business first; we will help them respond. To make a request, email "),
        em(),
        t(". You may also complain to the UAE Data Office."),
      ],
    },
    { kind: "h2", text: "Changes" },
    { kind: "p", spans: [t("We will post any update to this policy on this page and change the date at the top.")] },
    { kind: "h2", text: "Contact" },
    {
      kind: "p",
      spans: [t("Quintara Advertising Services L.L.C,Dubai, United Arab Emirates. Email: "), em(), t(". Phone: +971 52 132 8763.")],
    },
    { kind: "h2", text: "Government Data Request Policy" },
    {
      kind: "p",
      spans: [
        t("For information about how Quintara handles government and law enforcement requests for data, please review our "),
        link("Government Data Request Policy", "/government-data-request-policy"),
        t("."),
      ],
    },
  ],
};
